// ─────────────────────────────────────────────────────────────────────────────
// ZERO-CREDIT CMS — restaurantConfig
// Centralised data block. Edit values here to repurpose template for any client.
// ─────────────────────────────────────────────────────────────────────────────

export const restaurantConfig = {
  // ─── Identity ──────────────────────────────────────────────────────────────
  brand: {
    name: "La Brasserie Des Artistes",
    shortName: "Des Artistes",
    monogram: "BA",
    logo: "/images/La Brasserie logo.png",
    logoAlt: "La Brasserie Des Artistes",
    tagline: "Brasserie Française · Grasse",
    descriptor: "Maison de cuisine française authentique au cœur de la Provence.",
    established: "Depuis 1987",
  },

  // ─── Contact & address ─────────────────────────────────────────────────────
  contact: {
    address: "2 Pl. de la Foux, 06130 Grasse, France",
    addressLine1: "2 Pl. de la Foux",
    addressLine2: "06130 Grasse, France",
    phone: "033493400718",
    phoneDisplay: "+33 4 93 40 07 18",
    phoneLink: "tel:+33493400718",
    email: "contact@labrasseriedesartistes.fr",
  },

  // ─── Social & maps ─────────────────────────────────────────────────────────
  social: {
    facebook: "https://www.facebook.com/p/La-Brasserie-des-Artistes-61572618360992/",
    instagram: "#",
    tripadvisor: "#",
  },
  map: {
    locationUrl:
      "https://www.google.com/maps/place/La+Brasserie+Des+Artistes/@43.6604173,6.9225985,17z",
    embedSrc:
      "https://www.google.com/maps?q=2+Pl.+de+la+Foux,+06130+Grasse,+France&output=embed",
    lat: 43.6604173,
    lng: 6.9225985,
  },

  // ─── Operating hours ───────────────────────────────────────────────────────
  hours: [
    { day: "Lundi",    service: "Fermé" },
    { day: "Mardi",    service: "12h00 – 14h30   ·   19h00 – 22h30" },
    { day: "Mercredi", service: "12h00 – 14h30   ·   19h00 – 22h30" },
    { day: "Jeudi",    service: "12h00 – 14h30   ·   19h00 – 22h30" },
    { day: "Vendredi", service: "12h00 – 14h30   ·   19h00 – 23h00" },
    { day: "Samedi",   service: "12h00 – 15h00   ·   19h00 – 23h00" },
    { day: "Dimanche", service: "12h00 – 15h00" },
  ],

  // ─── Image assets (in /public/images/) ─────────────────────────────────────
  images: {
    atmosphere: [
      "/images/la-brasserie-des-artistes.jpg",
      "/images/la-brasserie-des-artistes (1).jpg",
      "/images/unnamed (9).webp",
    ],
    gourmet: [
      "/images/514346622_122146925630753945_7164949920273623359_n.jpg",
      "/images/516983857_122146925534753945_6812400307129002683_n.jpg",
    ],
    chalkboard: [
      "/images/491075783_122126325434753945_1932674753318011031_n.jpg",
      "/images/493579110_122130955580753945_5535094162106544884_n.jpg",
    ],
    extras: [
      "/images/482320125_122116281818753945_5077121380302281346_n.jpg",
      "/images/485413776_122119495058753945_2380709913651668966_n.jpg",
      "/images/unnamed (10).webp",
      "/images/unnamed (11).webp",
    ],
  },

  // ─── Hero (Accueil) ────────────────────────────────────────────────────────
  hero: {
    eyebrow: "Grasse · Côte d'Azur",
    title: "Une table de famille,\nune âme d'artiste.",
    subtitle:
      "Au cœur de Grasse, La Brasserie Des Artistes célèbre la cuisine française avec générosité — produits frais, recettes signatures et chaleur méridionale.",
    ctaPrimary: { label: "Réserver une table", to: "/reservation" },
    ctaSecondary: { label: "Découvrir la carte", to: "/menu" },
    image: "/images/la-brasserie-des-artistes.jpg",
    heroImages: [
      "/images/la-brasserie-des-artistes.jpg",
      "/images/la-brasserie-des-artistes (1).jpg",
      "/images/unnamed (9).webp",
      "/images/unnamed (10).webp",
    ],
    slideIntervalMs: 4500,
  },

  // ─── Notre Histoire ────────────────────────────────────────────────────────
  story: {
    eyebrow: "Notre Histoire",
    title: "Une brasserie où l'art\nrencontre la table.",
    paragraphs: [
      "Nichée Place de la Foux, La Brasserie Des Artistes perpétue depuis plusieurs années une tradition simple : recevoir comme à la maison, cuisiner avec amour, partager sans détour.",
      "Notre équipe sélectionne chaque matin les produits les plus frais auprès des producteurs locaux. La carte célèbre les classiques de la brasserie française — risottos onctueux, viandes grillées, poissons du marché, moules de saison.",
    ],
    signature: "Christelle & l'équipe",
    image: "/images/la-brasserie-des-artistes (1).jpg",
  },

  // ─── Chef's Highlights ─────────────────────────────────────────────────────
  highlights: {
    eyebrow: "Sélection du chef",
    title: "Les signatures de la maison",
    items: [
      {
        name: "Risotto St-Jacques & Gambas",
        description:
          "Risotto crémeux au parmesan, noix de St-Jacques snackées et gambas grillées, jus corsé au safran.",
        image: "/images/514346622_122146925630753945_7164949920273623359_n.jpg",
        tag: "Risotto signature",
      },
      {
        name: "Pavé de Dorade Grillé",
        description:
          "Dorade royale juste grillée, légumes de saison rôtis et huile d'olive vierge de Provence.",
        image: "/images/516983857_122146925534753945_6812400307129002683_n.jpg",
        tag: "Poisson du jour",
      },
      {
        name: "Tartare de Bœuf à la Provençale",
        description:
          "Bœuf coupé au couteau, câpres, échalote, basilic, huile d'olive et pointe d'anchois.",
        image: "/images/482276179_122116282046753945_1624932202318809515_n.jpg",
        tag: "Spécialité du chef",
      },
    ],
  },

  // ─── Testimonials (generic / curated) ──────────────────────────────────────
  testimonials: [
    {
      quote:
        "Une cuisine généreuse, un accueil chaleureux et un cadre charmant en plein cœur de Grasse. Une véritable adresse de famille.",
      author: "Camille D.",
      role: "Voyageuse",
    },
    {
      quote:
        "Le risotto aux Saint-Jacques était sublime, la dorade parfaitement cuite. Service attentionné, ambiance authentique. On reviendra.",
      author: "Antoine R.",
      role: "Habitué de la maison",
    },
    {
      quote:
        "Une brasserie comme on les aime — produits frais, plats généreux et une équipe qui sait recevoir. La Provence dans l'assiette.",
      author: "Élodie M.",
      role: "Critique gastronomique",
    },
    {
      quote:
        "Adresse incontournable à Grasse. Carte courte, savoureuse et bien pensée. Mention spéciale pour les moules à la thaï.",
      author: "Jean-Luc P.",
      role: "Visiteur régulier",
    },
  ],

  // ─── Menu (Carte) — items from brief ───────────────────────────────────────
  menu: [
    {
      id: "aperitifs",
      label: "Apéritifs & Boissons",
      blurb: "Pour ouvrir l'appétit et célébrer l'instant.",
      sections: [
        {
          title: "Apéritifs",
          items: [
            { name: "Sangria Maison", description: "Notre recette signature, fruits frais et vin rouge des coteaux.", price: "€7,50" },
            { name: "Kir Vin Blanc",  description: "Crème de cassis de Bourgogne, vin blanc sec.", price: "€6,00" },
          ],
        },
        {
          title: "Cocktails",
          items: [
            { name: "Aperol Spritz",  description: "Aperol, prosecco, eau gazeuse, tranche d'orange.", price: "€11,00" },
            { name: "Mojito",         description: "Rhum blanc, menthe fraîche, citron vert, sucre de canne.", price: "€11,00" },
            { name: "Virgin Mojito",  description: "Version sans alcool, menthe fraîche, citron vert, sucre de canne.", price: "€8,00" },
          ],
        },
      ],
    },
    {
      id: "entrees",
      label: "Entrées & Salades",
      blurb: "Légères, fraîches, savoureuses.",
      sections: [
        {
          title: "Entrées de saison",
          items: [
            { name: "Salade du Marché",        description: "Mesclun, tomates anciennes, copeaux de parmesan, vinaigrette balsamique.", price: "€12,00" },
            { name: "Burrata des Pouilles",    description: "Burrata crémeuse, tomates cerises confites, pesto maison, basilic.",     price: "€14,00" },
            { name: "Carpaccio de Bœuf",       description: "Fines tranches de bœuf, copeaux de parmesan, roquette, huile de truffe.",  price: "€15,00" },
          ],
        },
      ],
    },
    {
      id: "plats",
      label: "Plats Principaux",
      blurb: "Les classiques de la brasserie revisités.",
      sections: [
        {
          title: "Risottos",
          items: [
            { name: "Risotto St-Jacques et Gambas",     description: "Riz Carnaroli crémeux, noix de St-Jacques, gambas grillées.",       price: "€26,00" },
            { name: "Risotto Jambon Cru Fumé Parmesan", description: "Jambon cru fumé de Parme, copeaux de parmesan affiné 24 mois.",    price: "€21,00" },
          ],
        },
        {
          title: "Poissons",
          items: [
            { name: "Pavé de Dorade Grillé",        description: "Dorade royale, légumes de saison, huile d'olive vierge.",   price: "€24,00" },
            { name: "Brochette de Gambas Grillée",  description: "Gambas marinées aux herbes, riz pilaf, sauce vierge.",       price: "€25,00" },
          ],
        },
        {
          title: "Viandes",
          items: [
            { name: "Brochette d'Onglet de Bœuf Grillée", description: "Onglet de bœuf, échalote confite, frites maison.",       price: "€22,00" },
            { name: "Tartare de Bœuf à la Provençale",    description: "Bœuf coupé au couteau, câpres, basilic, huile d'olive.", price: "€21,00" },
          ],
        },
        {
          title: "Moules",
          items: [
            { name: "Moules Marinière",  description: "Vin blanc, échalote, persil, beurre, frites maison.",         price: "€18,00" },
            { name: "Moules Thaï",       description: "Lait de coco, citronnelle, gingembre, coriandre, frites.",    price: "€19,00" },
            { name: "Moules Espagnole",  description: "Chorizo, tomate, piquillos, ail, persil, frites maison.",     price: "€19,00" },
          ],
        },
      ],
    },
    {
      id: "enfants",
      label: "Pour Enfants",
      blurb: "Une carte pensée pour les plus jeunes gourmets.",
      sections: [
        {
          title: "Menu Enfant",
          items: [
            { name: "Steak haché frites",     description: "Steak haché, frites maison, ketchup ou mayonnaise.",  price: "€10,00" },
            { name: "Pâtes au beurre",        description: "Tagliatelles fraîches, beurre fermier, parmesan.",   price: "€8,00"  },
            { name: "Filet de poisson pané",  description: "Filet de cabillaud pané, purée maison.",             price: "€10,00" },
          ],
        },
      ],
    },
  ],

  // ─── Reservation form ──────────────────────────────────────────────────────
  reservation: {
    title: "Réservez votre table",
    subtitle:
      "Une simple confirmation par téléphone vous parviendra peu après. Pour les groupes de plus de 8 personnes, appelez-nous directement.",
    minGuests: 1,
    maxGuests: 10,
    timeSlots: [
      "12:00", "12:30", "13:00", "13:30", "14:00",
      "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00",
    ],
    successTitle: "Réservation reçue",
    successMessage:
      "Merci de votre confiance. Nous avons bien noté votre demande — vous recevrez une confirmation très prochainement.",
    fields: {
      date:   { label: "Date" },
      time:   { label: "Horaire" },
      guests: { label: "Convives", unit: "personne", unitPlural: "personnes" },
      name:   { label: "Nom complet",  placeholder: "Marie Dupont" },
      email:  { label: "E-mail",       placeholder: "vous@email.fr" },
      phone:  { label: "Téléphone",    placeholder: "+33 6 ..." },
      notes:  { label: "Notes (allergies, occasion...)", placeholder: "Une demande particulière ?" },
    },
    errors: {
      date:  "Veuillez choisir une date.",
      time:  "Veuillez choisir un horaire.",
      name:  "Votre nom complet est requis.",
      email: "Adresse e-mail invalide.",
      phone: "Votre téléphone est requis.",
    },
    submitLabel:    "Confirmer la réservation",
    smallNote:      "Confirmation envoyée sous quelques minutes.",
    closeLabel:     "Fermer",
    sideTitle1:     "Un accueil",
    sideTitleAccent:"sur-mesure",
    sideTitle2:     ".",
    sideBody:
      "Pour un anniversaire, un dîner d'affaires ou une privatisation, nos équipes vous répondent personnellement. N'hésitez pas à nous appeler.",
    summaryLabels: {
      date:    "Date :",
      time:    "Heure :",
      guests:  "Convives :",
      name:    "Nom :",
    },
  },

  // ─── UI Strings (page titles, eyebrows, button labels) ─────────────────────
  ui: {
    nav: [
      { to: "/",            label: "Accueil",     end: true  },
      { to: "/menu",        label: "Carte",       end: false },
      { to: "/galerie",     label: "Galerie",     end: false },
      { to: "/reservation", label: "Réservation", end: false },
      { to: "/contact",     label: "Contact",     end: false },
    ],
    reserveButton:   "Réserver",
    reserveLongCta:  "Réserver une table",
    scrollIndicator: "Faites défiler",
    themeToggle: {
      aria: "Changer le thème",
      options: [
        { value: "light",  label: "Clair"   },
        { value: "dark",   label: "Sombre"  },
        { value: "system", label: "Système" },
      ],
    },
    mobileMenuToggleAria: "Ouvrir le menu",
    menu: {
      eyebrow:   "La Carte",
      title:     "Notre Carte",
      subtitle:  "Une cuisine française authentique, des produits soigneusement sélectionnés, des recettes signature partagées avec passion.",
      footnote:  "Carte susceptible d'évoluer selon le marché et les saisons.",
    },
    gallery: {
      eyebrow:   "Galerie",
      title:     "L'ambiance, les plats,\nles moments.",
      subtitle:  "Une fenêtre sur l'âme de la maison — de la salle à la cuisine, en passant par les ardoises de saison.",
      closeAria: "Fermer",
      categories: {
        atmosphere: { category: "Ambiance", labels: ["L'atmosphère", "Notre salle", "Notre salle"] },
        gourmet:    { category: "Cuisine",  label:  "Création du chef" },
        chalkboard: { category: "Ardoise",  label:  "Suggestion du jour" },
        extras:     { category: "Maison",   label:  "L'instant Brasserie" },
      },
    },
    contact: {
      eyebrow:        "Contact",
      title:          "Nous trouver,\nnous parler.",
      subtitle:       "À deux pas du centre historique de Grasse, La Brasserie Des Artistes vous reçoit tous les jours sauf le lundi.",
      cards: {
        address: { title: "Adresse",   secondary: "",                                    cta: "Voir sur la carte" },
        phone:   { title: "Téléphone", secondary: "Pour réservations & informations",    cta: "Appeler maintenant" },
        email:   { title: "E-mail",    secondary: "Réponse sous 24 heures",              cta: "Nous écrire" },
      },
      hoursTitle:       "Horaires d'ouverture",
      hoursHeading:     "Quand venir",
      socialTitle:      "Rejoignez-nous",
      socialHeading:    "Sur les réseaux",
      socialBlurb:      "Suivez l'actualité de la maison, nos ardoises de saison, événements et invités — partagez vos plus belles tables.",
      socialItems: [
        { id: "facebook",  label: "Facebook",  handle: "La Brasserie des Artistes" },
        { id: "instagram", label: "Instagram", handle: "@labrasseriedesartistes"   },
      ],
      mapEyebrow:       "Nous situer",
      mapTitle:         "Place de la Foux, Grasse",
      mapOpenLabel:     "Ouvrir dans Google Maps",
      mapAria:          "Carte La Brasserie Des Artistes",
    },
    footer: {
      contactTitle:  "Contact",
      hoursTitle:    "Horaires",
      copyright:     "Tous droits réservés.",
      links: [
        { to: "/reservation", label: "Réserver"     },
        { to: "/menu",        label: "Carte"        },
        { to: "/contact",     label: "Nous trouver" },
      ],
    },
    home: {
      bannerEyebrow: "Réservez votre table",
      bannerTitle:   "Le plaisir d'une vraie table française vous attend.",
      bannerBody:    "Réservez en ligne en moins d'une minute, ou appelez-nous directement — nous serons heureux de vous accueillir.",
      testimonialsEyebrow:    "Ils en parlent",
      testimonialsTitle:      "Ce que disent nos invités",
      testimonialsPrevAria:   "Témoignage précédent",
      testimonialsNextAria:   "Témoignage suivant",
      historyMaisonLabel:     "Maison familiale",
      highlightsCta:          "Voir la carte complète",
    },
  },
};

export default restaurantConfig;
